// src/components/EditStudentDialog.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box,
  Avatar, Typography, CircularProgress, Alert
} from '@mui/material';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';

// 辅助函数：将 File 对象读取为 Base64 Data URL (保持不变)
const readFileAsDataURL = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

// onSave 现在是一个 async 函数，可能返回 true/false
function EditStudentDialog({ open, studentData, onSave, onClose }) {
  // 内部状态管理编辑后的数据
  const [editedName, setEditedName] = useState('');
  const [editedStudentId, setEditedStudentId] = useState(''); // 学号现在不可编辑
  const [editedFaceImageFile, setEditedFaceImageFile] = useState(null); // 新选择的图片 File 对象
  const [previewImageUrl, setPreviewImageUrl] = useState(null); // 预览 URL (旧服务器 URL 或新 Blob URL)
  const [isProcessing, setIsProcessing] = useState(false); // 处理状态 (用于按钮禁用和加载指示)
  const [error, setError] = useState(''); // 对话框内部错误 (例如图片选择问题)

  const imageInputRef = useRef(null);

  // 初始化表单状态
  useEffect(() => {
    if (open && studentData) {
      console.log("初始化编辑对话框，数据:", studentData);
      setEditedName(studentData.name || '');
      // --- 学号通常作为主键，不应允许编辑 ---
      setEditedStudentId(studentData.studentId || ''); // 显示学号，但不提供编辑字段
      setEditedFaceImageFile(null); // 重置新文件选择
      // 使用现有的服务器 URL 作为初始预览
      setPreviewImageUrl(studentData.faceImageUrl || null);
      setError('');
      setIsProcessing(false); // 确保每次打开时重置处理状态
    }
    // 清理 Blob URL 的逻辑保持不变
    const currentPreviewUrl = previewImageUrl;
    return () => {
        if (currentPreviewUrl && currentPreviewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(currentPreviewUrl);
            // console.log("Cleaned up edit preview Blob URL:", currentPreviewUrl);
        }
    }
  }, [open, studentData]); // 依赖于 open 和 studentData

  // 处理图片选择变化 (逻辑保持不变)
  const handleImageChange = (event) => {
    const file = event.target.files[0];
    setEditedFaceImageFile(null);
    setPreviewImageUrl(prevUrl => {
        if (prevUrl && prevUrl.startsWith('blob:')) {
            URL.revokeObjectURL(prevUrl);
        }
        return file ? null : (studentData?.faceImageUrl || null);
    });
    setError('');

    if (file && file.type.startsWith('image/')) {
      setEditedFaceImageFile(file);
      try {
        const url = URL.createObjectURL(file);
        setPreviewImageUrl(url);
      } catch (e) {
        console.error("创建新图片预览 URL 时出错:", e);
        setError("无法创建新图片预览。");
        setEditedFaceImageFile(null);
        setPreviewImageUrl(studentData?.faceImageUrl || null);
      }
    } else {
      if (file) setError('请选择一个有效的图片文件 (如 JPEG, PNG)。');
      else setError(''); // 取消选择时清除错误
      setPreviewImageUrl(studentData?.faceImageUrl || null);
    }
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  // 处理保存按钮点击
  const handleSaveChanges = async () => {
    if (!editedName) { // 学号不再由用户输入
      setError('学生姓名不能为空。');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      // --- 准备要传递给 onSave 的数据 ---
      let finalFaceImageUrl = studentData.faceImageUrl; // 默认使用旧的服务器图片 URL

      // 如果选择了新的图片文件，则转换为 Base64
      if (editedFaceImageFile) {
        console.log('正在将新选择的图片转换为 Base64...');
        finalFaceImageUrl = await readFileAsDataURL(editedFaceImageFile);
        console.log('新图片已转换为 Base64。');
      }

      // 准备更新后的学生数据对象，传递给 App.js 的 onSave (handleEditStudent)
      const updatedData = {
        // --- 包含原始学号 (studentId) 作为标识符 ---
        id: studentData.studentId, // 使用学号作为 id
        name: editedName,
        studentId: editedStudentId, // 传递学号 (虽然不可编辑)
        // --- 传递新的 Base64 或旧的服务器 URL ---
        faceImageUrl: finalFaceImageUrl,
      };

      // 调用从 StudentManagement -> App.js 传递过来的 onSave 回调
      // onSave 现在是一个 async 函数，会进行 API 调用并返回 true/false
      await onSave(updatedData); // App.js 中的 handleEditStudent 会处理 API 调用

      // 注意：关闭对话框的操作现在由 StudentManagement 在确认 onSave 成功后执行
      // 这里不再直接调用 onClose()

    } catch (err) {
      // 这个 catch 主要捕获 readFileAsDataURL 的错误
      console.error('编辑学生信息时出错 (本地处理):', err);
      setError(`处理图片数据时出错: ${err.message || '请稍后重试。'}`);
      // API 调用错误由 App.js 处理并在 App 级别显示
    } finally {
      setIsProcessing(false); // 无论成功失败，结束处理状态
    }
  };

  // 处理取消或关闭对话框 (X 按钮)
  const handleClose = () => {
    if (!isProcessing) {
       // 清理 Blob URL 的逻辑保持不变
       if (previewImageUrl && previewImageUrl.startsWith('blob:')) {
            URL.revokeObjectURL(previewImageUrl);
       }
       onClose(); // 调用从 StudentManagement 传递过来的 onClose 回调
    }
  };


  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>编辑学生信息</DialogTitle>
      <DialogContent>
        {/* 显示对话框内部错误 (例如图片格式错误) */}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {/* App 级别的 API 错误会在 App.js 中显示 */}

        {/* 学号字段 - 显示但禁用编辑 */}
        <TextField
          margin="dense"
          id="edit-studentId-display"
          label="学号 (不可修改)"
          type="text"
          fullWidth
          variant="outlined"
          value={editedStudentId}
          disabled // 始终禁用
          sx={{ mb: 2 }}
        />
         {/* 姓名 */}
        <TextField
          autoFocus // 自动聚焦到姓名输入框
          margin="dense"
          id="edit-name"
          label="学生姓名"
          type="text"
          fullWidth
          variant="outlined"
          value={editedName}
          onChange={(e) => setEditedName(e.target.value)}
          disabled={isProcessing} // 在处理时禁用
          sx={{ mb: 2 }}
        />

         {/* 图片上传和预览 */}
         <Box sx={{ mt: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
           <input
            type="file"
            accept="image/*"
            ref={imageInputRef}
            onChange={handleImageChange}
            style={{ display: 'none' }}
            id="edit-face-image-upload"
          />
          <Button
            variant="outlined"
            startIcon={<AddAPhotoIcon />}
            disabled={isProcessing}
            component="label"
            htmlFor="edit-face-image-upload"
            size="small"
          >
            更换图片
          </Button>
           {/* 预览区域 */}
           {previewImageUrl ? (
             <Avatar
                // src 可以是旧的服务器 URL (http://...) 或新的本地 Blob URL (blob:...)
                src={previewImageUrl}
                sx={{ width: 60, height: 60 }}
                variant="rounded"
                alt="人脸预览"
                // 添加 onError 处理，例如在服务器 URL 失效时
                onError={(e) => {
                    console.warn("编辑预览头像加载失败", previewImageUrl);
                    // 可以设置一个默认图片或隐藏 Avatar
                    // e.target.src = '/path/to/default/avatar.png';
                    e.target.style.display = 'none'; // 简单隐藏
                    setError("头像预览加载失败，可能是无效的 URL。");
                }}
            />
          ) : (
            <Avatar sx={{ width: 60, height: 60, bgcolor: 'grey.300' }} variant="rounded">
                <Typography variant="caption" align="center" sx={{ fontSize: '0.7rem', p: 0.5 }}>无图片</Typography>
            </Avatar>
          )}
           {/* 显示新选择的文件名 (逻辑保持不变) */}
           {editedFaceImageFile && (
             <Typography variant="body2" sx={{ flexGrow: 1, fontStyle: 'italic', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                新: {editedFaceImageFile.name}
            </Typography>
           )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        {/* 取消按钮 */}
        <Button onClick={handleClose} disabled={isProcessing} color="secondary">
          取消
        </Button>
        {/* 保存按钮 */}
        <Button onClick={handleSaveChanges} disabled={isProcessing} variant="contained">
          {isProcessing ? <CircularProgress size={24} color="inherit" /> : '保存更改'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditStudentDialog;